/**
 * Merges Samsara geofence timing with Track-POD's driver record at read time. Samsara wins wherever
 * it has a value; Track-POD only fills silence (except serviced-vs-skipped, which resolves to completed).
 */
import type { StopInstance, StopInstanceStatus } from "@/types/collections"

import {
  TRACKPOD_NOT_SERVICED_STATUSES,
  TRACKPOD_SERVICED_STATUSES,
} from "../../supabase/functions/_shared/trackpodStatus.ts"
import { isPlausibleWithin, toDate } from "./timingResolution"

/** `inferred` applies to status only: later arrivals prove a stop was passed, never when. */
export type TimingSource = "samsara" | "trackpod" | "inferred" | "none"

/** What may honestly be said about a stop's departure; `on_site` deliberately carries no time. */
export type DepartureProjection =
  | { kind: "actual"; at: Date }
  | { kind: "eta"; at: Date }
  | { kind: "on_site"; since: Date }
  /** Finished, but neither system recorded when it left. */
  | { kind: "unrecorded" }
  | { kind: "planned"; at: Date }

export interface ResolvedStopTiming {
  arrival?: Date
  arrivalSource: TimingSource
  departure?: Date
  departureSource: TimingSource
  departureProjection: DepartureProjection
  status: StopInstanceStatus
  statusSource: TimingSource
  /** GPS and driver disagree: status resolved away from Samsara's, or a Track-POD-only arrival. */
  disagreement: boolean
}

/** Wide on purpose: catches wrong-day/timezone entry errors without discarding real lateness. */
export const TRACKPOD_PLAUSIBILITY_WINDOW_HOURS = 12


/**
 * Gate on trusting a Track-POD departure: orders are created "In Progress" with a status date,
 * so an unopened stop can carry a phantom departure. Not applied to arrivals.
 */
const DRIVER_RECORDED = new Set([
  ...TRACKPOD_SERVICED_STATUSES,
  ...TRACKPOD_NOT_SERVICED_STATUSES,
])

/** Judged against planned arrival, since an unstarted route is where Track-POD is the only evidence. */
function isPlausible(candidate: Date, plannedArrival: Date): boolean {
  return isPlausibleWithin(candidate, plannedArrival, TRACKPOD_PLAUSIBILITY_WINDOW_HOURS)
}

export function resolveStopTiming(stop: StopInstance, now: Date = new Date()): ResolvedStopTiming {
  const samsaraArrival = toDate(stop.actual_arrival)
  const samsaraDeparture = toDate(stop.actual_departure)
  const plannedArrival = toDate(stop.planned_arrival)
  const plannedDeparture = toDate(stop.planned_departure)

  const rawTpArrival = toDate(stop.trackpod_arrival)
  const rawTpDeparture = toDate(stop.trackpod_departure)
  const tpStatus = stop.trackpod_status ?? undefined

  // Implausible values are treated as absent everywhere, including status resolution.
  const tpArrival =
    rawTpArrival && plannedArrival && isPlausible(rawTpArrival, plannedArrival)
      ? rawTpArrival
      : undefined

  // Departure before arrival is the most common manual-entry error.
  const effectiveArrivalForOrdering = samsaraArrival ?? tpArrival
  const tpDeparture =
    rawTpDeparture &&
    plannedArrival &&
    tpStatus &&
    DRIVER_RECORDED.has(tpStatus) &&
    isPlausible(rawTpDeparture, plannedDeparture ?? plannedArrival) &&
    !(effectiveArrivalForOrdering && rawTpDeparture < effectiveArrivalForOrdering)
      ? rawTpDeparture
      : undefined

  const arrival = samsaraArrival ?? tpArrival
  const arrivalSource: TimingSource = samsaraArrival ? "samsara" : tpArrival ? "trackpod" : "none"

  const departure = samsaraDeparture ?? tpDeparture
  const departureSource: TimingSource = samsaraDeparture
    ? "samsara"
    : tpDeparture
      ? "trackpod"
      : "none"

  const { status, statusSource, statusDisagrees } = resolveStatus(
    stop,
    tpStatus,
    tpArrival,
    tpDeparture,
    now
  )

  return {
    arrival,
    arrivalSource,
    departure,
    departureSource,
    departureProjection: projectDeparture(stop, arrival, departure, status),
    status,
    statusSource,
    // Departure-only gaps aren't a conflict: Samsara routinely has arrival without departure mid-stop.
    disagreement: statusDisagrees || arrivalSource === "trackpod",
  }
}

/**
 * An arrival with no departure means the van is there, so a stale Samsara ETA never survives it.
 * The eta departure is derived from our planned window (first/last stops have no stored one).
 */
function projectDeparture(
  stop: StopInstance,
  arrival: Date | undefined,
  departure: Date | undefined,
  status: StopInstanceStatus
): DepartureProjection {
  if (departure) return { kind: "actual", at: departure }

  // Read off status: a finished stop with arrival but no departure is not still on site.
  if (arrival) {
    const finished = status === "completed" || status === "skipped"
    return finished ? { kind: "unrecorded" } : { kind: "on_site", since: arrival }
  }

  const estimatedArrival = toDate(stop.estimated_arrival)
  if (estimatedArrival) {
    const stored = toDate(stop.estimated_departure)
    if (stored) return { kind: "eta", at: stored }

    const plannedArrival = toDate(stop.planned_arrival)
    const plannedDeparture = toDate(stop.planned_departure)
    const plannedWindowMs =
      plannedArrival && plannedDeparture ? plannedDeparture.getTime() - plannedArrival.getTime() : 0
    // Zero or inverted planned window (ad-hoc stop): fall back to service time.
    const windowMs = plannedWindowMs > 0 ? plannedWindowMs : stop.service_time_minutes * 60_000
    return { kind: "eta", at: new Date(estimatedArrival.getTime() + windowMs) }
  }

  return { kind: "planned", at: new Date(stop.planned_departure) }
}

function resolveStatus(
  stop: StopInstance,
  tpStatus: string | undefined,
  tpArrival: Date | undefined,
  tpDeparture: Date | undefined,
  now: Date
): { status: StopInstanceStatus; statusSource: TimingSource; statusDisagrees: boolean } {
  const samsara = stop.status
  const keep = { status: samsara, statusSource: "samsara" as TimingSource, statusDisagrees: false }

  if (!tpStatus) return keep

  const serviced = TRACKPOD_SERVICED_STATUSES.has(tpStatus)
  const notServiced = TRACKPOD_NOT_SERVICED_STATUSES.has(tpStatus)

  // Geofence miss: driver evidence of collection beats GPS absence of evidence.
  if (serviced && samsara === "skipped") {
    return { status: "completed", statusSource: "trackpod", statusDisagrees: true }
  }

  // Never fired at all: only trusted once planned departure is past, or with a driver arrival.
  if (serviced && (samsara === "scheduled" || samsara === "en_route")) {
    const plannedDeparture = toDate(stop.planned_departure)
    const isPast = plannedDeparture ? now > plannedDeparture : false
    if (isPast || tpArrival) {
      return { status: "completed", statusSource: "trackpod", statusDisagrees: true }
    }
  }

  // Arrival registered, exit missed: a Track-POD departure is enough to close it.
  if (serviced && (samsara === "arrived" || samsara === "processing") && tpDeparture) {
    return { status: "completed", statusSource: "trackpod", statusDisagrees: true }
  }

  // Driver's explicit "not collected" on a stop Samsara never registered; same gate as serviced.
  if (notServiced && (samsara === "scheduled" || samsara === "en_route")) {
    const plannedDeparture = toDate(stop.planned_departure)
    const isPast = plannedDeparture ? now > plannedDeparture : false
    if (isPast || tpArrival) {
      return { status: "skipped", statusSource: "trackpod", statusDisagrees: true }
    }
  }

  // Attended with nothing collected: Samsara keeps status, but it's flagged.
  if (notServiced && samsara === "completed") {
    return { status: samsara, statusSource: "samsara", statusDisagrees: true }
  }

  return keep
}

/** Samsara records status and departure independently, so either one counts. */
export function hasStopDeparted(stop: {
  status: StopInstanceStatus
  actualDeparture?: Date
}): boolean {
  return (
    stop.status === "completed" || stop.status === "skipped" || stop.actualDeparture !== undefined
  )
}

/** Asked of the resolved status, so a route closed in Track-POD counts even if Samsara never saw the depot. */
export function isRouteOver(route: { status: string }): boolean {
  return route.status === "completed" || route.status === "cancelled"
}

/** Phone-vs-gateway clock skew only; displaced claims land 20m to 5h out. */
export const TRACKPOD_SEQUENCE_TOLERANCE_MINUTES = 5

/**
 * Reverts a Track-POD-promoted stop to skipped when its claimed arrival falls outside the measured
 * (Samsara) times of its neighbours, i.e. the driver cleared it from elsewhere. Drops the time too.
 */
export function rejectDisplacedTrackpodClaims<
  T extends {
    actualArrival?: Date
    actualDeparture?: Date
    plannedDeparture: Date
    arrivalSource?: TimingSource
    departureSource?: TimingSource
    status: StopInstanceStatus
    statusSource?: TimingSource
    hasSourceDisagreement?: boolean
    departureProjection?: DepartureProjection
  },
>(stops: T[]): T[] {
  const toleranceMs = TRACKPOD_SEQUENCE_TOLERANCE_MINUTES * 60_000

  const measured = (stop: T) => stop.arrivalSource === "samsara" && stop.actualArrival

  return stops.map((stop, i) => {
    if (stop.statusSource !== "trackpod") return stop
    if (stop.arrivalSource !== "trackpod" || !stop.actualArrival) return stop

    const claimed = stop.actualArrival.getTime()

    const before = stops.slice(0, i).filter(measured).pop()
    const after = stops.slice(i + 1).find(measured)

    // Lower bound is the previous stop's departure where there is one.
    const earliest = before
      ? (before.actualDeparture ?? before.actualArrival!).getTime()
      : undefined
    const latest = after ? after.actualArrival!.getTime() : undefined

    const tooEarly = earliest !== undefined && claimed < earliest - toleranceMs
    const tooLate = latest !== undefined && claimed > latest + toleranceMs
    if (!tooEarly && !tooLate) return stop

    return {
      ...stop,
      status: "skipped" as StopInstanceStatus,
      statusSource: "samsara" as TimingSource,
      actualArrival: undefined,
      actualDeparture: undefined,
      arrivalSource: "none" as TimingSource,
      departureSource: "none" as TimingSource,
      departureProjection: { kind: "planned" as const, at: stop.plannedDeparture },
      hasSourceDisagreement: true,
    }
  })
}

/**
 * Marks stops before the last real arrival as completed (status only, never a time). With
 * `routeClosed`, also fills stops after it, but only ones Track-POD was never given.
 */
export function inferPassedStops<
  T extends {
    actualArrival?: Date
    status: StopInstanceStatus
    statusSource?: TimingSource
    type?: string
    trackpodIsOrder?: boolean
  },
>(stops: T[], options: { routeClosed?: boolean } = {}): T[] {
  /** Uses the export's `trackpodIsOrder`; stop type is only the fallback before any export. */
  const outsideTrackpod = (stop: T) =>
    stop.trackpodIsOrder === undefined
      ? stop.type === "depot" || stop.type === "fuel" || stop.type === "break"
      : !stop.trackpodIsOrder

  const lastObserved = stops.reduce(
    (last, stop, i) => (stop.actualArrival || stop.status === "completed" ? i : last),
    -1
  )
  if (lastObserved < 0) return stops

  if (options.routeClosed) {
    return stops.map((stop, i) => {
      if (stop.actualArrival) return stop
      if (stop.status !== "scheduled" && stop.status !== "en_route") return stop
      if (i > lastObserved && !outsideTrackpod(stop)) return stop
      return {
        ...stop,
        status: "completed" as StopInstanceStatus,
        statusSource: "inferred" as const,
      }
    })
  }

  return stops.map((stop, i) => {
    if (i >= lastObserved) return stop
    if (stop.actualArrival) return stop
    // Only silence is filled; arrived/processing aren't ours to overrule without a timestamp.
    if (stop.status !== "scheduled" && stop.status !== "en_route") return stop
    return { ...stop, status: "completed" as StopInstanceStatus, statusSource: "inferred" as const }
  })
}
